import React, { memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useAppContext } from 'src/components/app-context';
import { Breadcrumbs } from '@prisjakt/ui/breadcrumbs';
import { Translate } from 'src/components/translate';

import actions from '../actions';

import { PageContent } from 'src/components/page';
import type { RequiredNotNull } from 'src/utils/types';
import type { AppState } from 'src/reducers';
import type {
  AnalyticsItem,
  BrandPageFragment,
  BrandPageQuery,
  BrandProductListingFragment,
  CategoryListingFragment,
  CategoryTreeListingFragment,
} from 'src/types';
import BrandContent from './brand-content';
import FeaturedBrandContent from './featured-brand-content';
import PremiumBrandContent from './premium-brand-content';
import {
  trackInternalNavigationClick,
  InternalNavigationScope,
} from 'src/globals/tracking/tracking';
import { useBrandPageQuery } from '../queries/useBrandPage';
import { Link } from 'react-router-dom';

import Loader from 'src/components/loader';
import { useMatchedRoute } from 'src/hooks/use-matched-route';
import { AnalyticsButton, BreadCrumbWrapper, Content } from './styles';

export type BrandContentProps = Pick<
  BrandPageFragment,
  'id' | 'name' | 'externalUri' | 'externalUriText' | 'logo'
> &
  Pick<BrandProductListingFragment, 'products'> &
  Pick<CategoryListingFragment, 'categories'> &
  Pick<CategoryTreeListingFragment, 'categoryTree'> & {
    brandLinkClick: () => void;
    analyticsAccessLink?: JSX.Element;
  };

export const BrandPageLoader = () => {
  const route = useMatchedRoute();
  const variables = { id: route?.match?.params?.id ?? '' };
  const { data, isLoading, isSuccess } = useBrandPageQuery(variables);

  if (!isLoading && !isSuccess) {
    return null;
  }

  return (
    <PageContent withAds data-test="BrandPage">
      {isLoading ? <Loader /> : null}
      {isSuccess && data.brand ? <BrandPage brand={data.brand} /> : null}
    </PageContent>
  );
};

const BrandPage = ({
  brand,
}: RequiredNotNull<Pick<BrandPageQuery, 'brand'>>) => {
  const { t } = useAppContext();
  const dispatch = useDispatch();
  const { brandLinkClick } = actions;
  const {
    id,
    name,
    featured,
    featuredProducts,
    premium,
    logo,
    products,
    categories,
    categoryTree,
    externalUri,
    externalUriText,
    header,
    description,
    media,
  } = brand;

  const analyticsAccess = useSelector((state: AppState) =>
    state.areas.account.me?.analyticsAccess?.brands?.find(
      (item: AnalyticsItem) => item?.id === id
    )
  );
  const analyticsAccessLink = analyticsAccess && (
    <AnalyticsButton href={analyticsAccess.url}>
      <Translate domain="account">menu.pricespy-brand-analytics</Translate>
    </AnalyticsButton>
  );

  const brandLinkClickCallback = () =>
    dispatch(brandLinkClick({ id: Number(id), name, featured }));

  let content;
  if (premium) {
    content = (
      <PremiumBrandContent
        id={Number(id)}
        name={name}
        logo={logo}
        externalUri={externalUri}
        externalUriText={externalUriText}
        brandLinkClick={brandLinkClickCallback}
        products={products}
        categories={categories}
        categoryTree={categoryTree}
        header={header}
        description={description}
        featuredProducts={featuredProducts}
        media={media}
        analyticsAccessLink={analyticsAccessLink}
      />
    );
  } else if (featured) {
    content = (
      <FeaturedBrandContent
        id={Number(id)}
        name={name}
        logo={logo}
        externalUri={externalUri}
        externalUriText={externalUriText}
        brandLinkClick={brandLinkClickCallback}
        products={products}
        categories={categories}
        categoryTree={categoryTree}
        analyticsAccessLink={analyticsAccessLink}
      />
    );
  } else {
    content = (
      <BrandContent
        id={Number(id)}
        name={name}
        logo={logo}
        externalUri={externalUri}
        externalUriText={externalUriText}
        brandLinkClick={brandLinkClickCallback}
        products={products}
        categories={categories}
        categoryTree={categoryTree}
        analyticsAccessLink={analyticsAccessLink}
      />
    );
  }

  return (
    <>
      <BreadCrumbWrapper>
        <Breadcrumbs
          containerProps={{
            onClick: (nextPage: string) => {
              trackInternalNavigationClick({
                scope: InternalNavigationScope.BREADCRUMB,
                nextPage,
              });
            },
          }}
        >
          <Link to="/">{t('common', 'breadcrumb.home')}</Link>
          <span>{name}</span>
        </Breadcrumbs>
      </BreadCrumbWrapper>
      <Content>{content}</Content>
    </>
  );
};

export default memo(BrandPageLoader);
